/**
 * 수정제안 그리기 상태머신 훅 — 필드 우선 방식 (M15 / A10, 2026-06-02 재설계).
 *
 * 흐름 (설계 §7.3 개정):
 *   우측 패널에서 필드의 "위치 표시" 클릭 → startField(field)
 *     → stage=label : 라벨 영역 드래그 (또는 "라벨 없음" 스킵)
 *          → stage=value : 값 영역 드래그
 *               → 그룹 확정 (필드는 이미 정해져 있음) + activeField 해제
 *
 * 그린 뒤 필드를 고르던 기존 방식(FieldPicker)을 폐기하고, "필드 먼저 선택 →
 * 영역 그리기" 로 역전해 직관성을 높였다.
 *
 * 정책: 한 필드당 한 위치 (재선택 시 기존 그룹 교체) — 우측 필드 목록과 1:1.
 */

import { useCallback, useState } from "react";

import type { EditableFieldKey } from "../fieldLabels";
import type { Bbox, ClientAnnotation, DrawStage } from "./types";

let _seq = 0;
function nextId(): string {
  _seq += 1;
  return `ann-${Date.now()}-${_seq}`;
}

export interface UseAnnotationDrawing {
  groups: ClientAnnotation[];
  /** 현재 그리는 대상 필드. null 이면 그리기 비활성 (보기 모드). */
  activeField: EditableFieldKey | null;
  stage: DrawStage;
  pendingLabel: Bbox | null;

  /** 필드 선택 → 라벨 단계로 그리기 시작. */
  startField: (field: EditableFieldKey) => void;
  /** 라벨 박스 확정 → 값 단계. */
  commitLabel: (bbox: Bbox) => void;
  /** 라벨 스킵 → 값 단계. */
  skipLabel: () => void;
  /** 값 박스 확정 → 그룹 생성(필드 동일 기존 그룹 교체) + 그리기 종료. */
  commitValue: (bbox: Bbox, page: number) => void;
  /** 현재 필드 그리기 취소. */
  cancelField: () => void;
  /** 그룹 삭제. */
  removeGroup: (id: string) => void;
  reset: () => void;
}

export function useAnnotationDrawing(initial: ClientAnnotation[] = []): UseAnnotationDrawing {
  const [groups, setGroups] = useState<ClientAnnotation[]>(initial);
  const [activeField, setActiveField] = useState<EditableFieldKey | null>(null);
  const [stage, setStage] = useState<DrawStage>("label");
  const [pendingLabel, setPendingLabel] = useState<Bbox | null>(null);

  const startField = useCallback((field: EditableFieldKey) => {
    setActiveField(field);
    setStage("label");
    setPendingLabel(null);
  }, []);

  const commitLabel = useCallback((bbox: Bbox) => {
    setPendingLabel(bbox);
    setStage("value");
  }, []);

  const skipLabel = useCallback(() => {
    setPendingLabel(null);
    setStage("value");
  }, []);

  const commitValue = useCallback(
    (bbox: Bbox, page: number) => {
      setActiveField((field) => {
        if (field == null) return null;
        const group: ClientAnnotation = {
          id: nextId(),
          field,
          page,
          labelBbox: pendingLabel,
          valueBbox: bbox,
        };
        // 같은 필드의 기존 그룹은 교체 (한 필드당 한 위치).
        setGroups((prev) => [...prev.filter((g) => g.field !== field), group]);
        return null;
      });
      setStage("label");
      setPendingLabel(null);
    },
    [pendingLabel],
  );

  const cancelField = useCallback(() => {
    setActiveField(null);
    setStage("label");
    setPendingLabel(null);
  }, []);

  const removeGroup = useCallback((id: string) => {
    setGroups((prev) => prev.filter((g) => g.id !== id));
  }, []);

  const reset = useCallback(() => {
    setGroups([]);
    setActiveField(null);
    setStage("label");
    setPendingLabel(null);
  }, []);

  return {
    groups,
    activeField,
    stage,
    pendingLabel,
    startField,
    commitLabel,
    skipLabel,
    commitValue,
    cancelField,
    removeGroup,
    reset,
  };
}
